// Push notification subscription orchestration for the Settings page.
//
// Three states the user sees:
//   - unsupported: this browser does not have the Push API. Mostly old
//     iOS Safari pre-PWA; we show the "add to home screen" hint card.
//   - denied: the user revoked permission. Tell them how to re-enable
//     in browser settings.
//   - prompt: ask them to enable; calling enable() shows the browser
//     permission prompt, then subscribes and POSTs the envelope to
//     /api/push/subscribe.
//   - granted: subscribed. Show a "Disable" button and the test button.
//
// We never auto-prompt for permission — that's a senior-UX antipattern
// and Safari penalizes it. The user has to tap "Turn on" first.

import { useCallback, useEffect, useState } from 'react';
import { env } from '../env';
import { useAuthedFetch } from './useAuthedFetch';

export type PushPermissionState =
  | 'unsupported'
  | 'denied'
  | 'prompt'
  | 'granted'
  | 'loading'
  | 'not-configured';

interface PushApiState {
  state: PushPermissionState;
  /** True when an existing subscription was found server-side. */
  subscribed: boolean;
  /** When the operator hasn't set VAPID, no toggle is shown. */
  configured: boolean;
}

function isSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** Convert a base64url VAPID public key into the Uint8Array the
 *  browser's PushManager.subscribe expects. */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function usePushSubscription(): {
  state: PushApiState;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
  test: () => Promise<void>;
  error: string | null;
} {
  const authedFetch = useAuthedFetch();
  const [state, setState] = useState<PushApiState>({
    state: 'loading',
    subscribed: false,
    configured: true,
  });
  const [error, setError] = useState<string | null>(null);

  // Detect current permission + subscription status on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isSupported()) {
        setState({ state: 'unsupported', subscribed: false, configured: true });
        return;
      }
      try {
        const res = await fetch(`${env.apiBaseUrl}/api/push/vapid-public-key`);
        const body = (await res.json()) as {
          ok: boolean;
          data?: { publicKey: string | null; configured: boolean };
        };
        const configured = Boolean(body.data?.configured);
        if (!configured) {
          if (!cancelled)
            setState({ state: 'not-configured', subscribed: false, configured: false });
          return;
        }
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = (await reg?.pushManager.getSubscription()) ?? null;
        const permission = Notification.permission;
        const next: PushApiState = {
          state:
            permission === 'denied'
              ? 'denied'
              : permission === 'granted'
                ? sub
                  ? 'granted'
                  : 'prompt'
                : 'prompt',
          subscribed: Boolean(sub),
          configured: true,
        };
        if (!cancelled) setState(next);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const enable = useCallback(async () => {
    setError(null);
    if (!isSupported()) return;
    try {
      // 1. Register the service worker if it isn't already.
      const reg =
        (await navigator.serviceWorker.getRegistration()) ??
        (await navigator.serviceWorker.register('/service-worker.js', { scope: '/' }));
      await navigator.serviceWorker.ready;

      // 2. Ask the user. This shows the browser-native permission
      //    sheet — Safari requires this to be inside a user gesture.
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setState((s) => ({ ...s, state: permission === 'denied' ? 'denied' : 'prompt' }));
        return;
      }

      // 3. Fetch the public VAPID key.
      const res = await fetch(`${env.apiBaseUrl}/api/push/vapid-public-key`);
      const body = (await res.json()) as {
        ok: boolean;
        data?: { publicKey: string | null; configured: boolean };
      };
      const publicKey = body.data?.publicKey;
      if (!publicKey) {
        setError('Push notifications are not configured on this server yet.');
        setState({ state: 'not-configured', subscribed: false, configured: false });
        return;
      }

      // 4. Subscribe on the browser side. The applicationServerKey
      //    cast is needed because lib.dom narrowed BufferSource to
      //    ArrayBuffer-backed views — Uint8Array is fine at runtime.
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as unknown as BufferSource,
      });

      // 5. Send the subscription envelope to the server.
      const json = sub.toJSON();
      const send = await authedFetch('/api/push/subscribe', {
        method: 'POST',
        body: JSON.stringify({
          endpoint: json.endpoint,
          keys: json.keys,
        }),
      });
      if (!send.ok) {
        const e = (await send.json().catch(() => ({}))) as {
          error?: { message?: string };
        };
        throw new Error(e.error?.message ?? `HTTP ${send.status}`);
      }
      setState({ state: 'granted', subscribed: true, configured: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [authedFetch]);

  const disable = useCallback(async () => {
    setError(null);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager.getSubscription();
      if (sub) {
        await authedFetch('/api/push/unsubscribe', {
          method: 'POST',
          body: JSON.stringify({ endpoint: sub.endpoint }),
        }).catch(() => null);
        await sub.unsubscribe().catch(() => null);
      }
      setState((s) => ({ ...s, state: 'prompt', subscribed: false }));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [authedFetch]);

  const test = useCallback(async () => {
    setError(null);
    try {
      const res = await authedFetch('/api/push/test', { method: 'POST' });
      if (!res.ok) {
        const e = (await res.json().catch(() => ({}))) as {
          error?: { message?: string };
        };
        throw new Error(e.error?.message ?? `HTTP ${res.status}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [authedFetch]);

  return { state, enable, disable, test, error };
}
